import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories, allCourses } from '@/mocks/data';
import { useState, useEffect } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'it-programming');
  const [selectedLevel, setSelectedLevel] = useState('全部');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const filteredCourses = allCourses.filter((c) => c.category === activeSlug);
  const levelFiltered = selectedLevel === '全部'
    ? filteredCourses
    : filteredCourses.filter((c) => c.level === selectedLevel);

  const allLevels = ['全部', ...new Set(filteredCourses.map((c) => c.level))];

  const seoTitle = `${activeCategory?.name || '课程分类'}——成人在线课程`;
  const seoDesc = activeCategory?.description || '选择适合你的成人在线课程';
  const seoKeywords = `成人在线课程,${activeCategory?.name},在线职业技能培训`;

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: levelFiltered.map((course, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Course',
        name: course.title,
        description: course.description,
        url: `https://www.example.com/article/${course.slug}`,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
      { '@type': 'ListItem', position: 2, name: activeCategory?.name || '课程分类', item: `https://www.example.com/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`https://www.example.com/category/${activeSlug}`}
        schemaData={{ '@context': 'https://schema.org', '@graph': [itemListSchema, breadcrumbSchema] }}
      />
      <Navbar />

      <main>
        <section className="pt-24 pb-8 md:pt-32 md:pb-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: activeCategory?.name || '课程分类' },
              ]}
            />

            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-3">
              {activeCategory?.name}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl">
              {activeCategory?.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Level Filter & Course Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <span className="text-sm text-foreground-500 mr-1">难度：</span>
              {allLevels.map((level) => (
                <button
                  key={level}
                  onClick={() => setSelectedLevel(level)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedLevel === level
                      ? 'bg-secondary-500 text-background-50'
                      : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>

            {levelFiltered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该难度下暂无课程</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {levelFiltered.map((course) => (
                  <Link
                    key={course.id}
                    to={`/article/${course.slug}`}
                    className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-700">
                        {course.level}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-foreground-600 text-sm line-clamp-2 mb-4 leading-relaxed">{course.description}</p>

                      <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {course.duration}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i> {course.students.toLocaleString()}人
                        </span>
                        <span className="flex items-center gap-1 text-primary-600">
                          <i className="ri-star-fill"></i> {course.rating}
                        </span>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-background-200">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-bold text-primary-600">¥{course.price}</span>
                          <span className="text-sm text-foreground-400 line-through">¥{course.originalPrice}</span>
                        </div>
                        <span className="text-xs text-foreground-500">{course.instructor}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}